import apiClient from './apiClient';
import { logger } from '../utils/logger'
import {
  MercadoPagoSettings, 
  MercadoPagoSettingsRequest, 
  MercadoPagoTestResult, 
  MercadoPagoStats,
  MercadoPagoApiResponse 
} from '../types/mercadopago.types';

class MercadoPagoService {
  private baseURL = '/mercadopago-settings';

  /**
   * Obter configurações do MercadoPago
   */
  async getSettings(): Promise<MercadoPagoSettings> {
    const maxRetries = 3;
    let lastError: any;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await apiClient.get<MercadoPagoApiResponse<MercadoPagoSettings>>(this.baseURL);
        
        if (!response.data.success || !response.data.data) {
          throw new Error(response.data.error || 'Erro ao obter configurações');
        }

        return response.data.data;
      } catch (error: any) {
        lastError = error;
        logger.error(`Erro ao obter configurações do MercadoPago (tentativa ${attempt}/${maxRetries}):`, error);
        
        if (attempt < maxRetries) {
          // Aguardar antes da próxima tentativa
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
      }
    }

    throw new Error(lastError?.response?.data?.error || 'Erro ao obter configurações após múltiplas tentativas');
  }

  /**
   * Atualizar configurações do MercadoPago
   */
  async updateSettings(settings: MercadoPagoSettingsRequest): Promise<MercadoPagoSettings> {
    try {
      const response = await apiClient.put<MercadoPagoApiResponse<MercadoPagoSettings>>(
        this.baseURL, 
        settings
      );
      
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || 'Erro ao atualizar configurações');
      }

      return response.data.data;
    } catch (error: any) {
      logger.error('Erro ao atualizar configurações do MercadoPago:', error);
      throw new Error(error.response?.data?.error || 'Erro ao atualizar configurações');
    }
  }

  /**
   * Testar conexão com MercadoPago
   */
  async testConnection(): Promise<MercadoPagoTestResult> {
    try {
      const response = await apiClient.post<MercadoPagoApiResponse<MercadoPagoTestResult>>(
        `${this.baseURL}/test-connection`
      );
      
      if (!response.data.success || !response.data.data) {
        const errorMessage = response.data.error || response.data.message || 'Erro ao testar conexão';
        throw new Error(errorMessage);
      }

      return response.data.data;
    } catch (error: any) {
      logger.error('Erro ao testar conexão com MercadoPago:', error);
      const backendMessage = 
        error.response?.data?.error || 
        error.response?.data?.message || 
        error.message || 
        'Erro ao testar conexão';
      throw new Error(backendMessage);
    }
  }

  /**
   * Obter estatísticas de pagamentos
   */
  async getPaymentStats(): Promise<MercadoPagoStats> {
    const maxRetries = 3;
    let lastError: any;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await apiClient.get<MercadoPagoApiResponse<MercadoPagoStats>>(
          `${this.baseURL}/stats`
        );
        
        if (!response.data.success || !response.data.data) {
          throw new Error(response.data.error || 'Erro ao obter estatísticas');
        }

        return response.data.data;
      } catch (error: any) {
        lastError = error;
        logger.error(`Erro ao obter estatísticas de pagamentos (tentativa ${attempt}/${maxRetries}):`, error);
        
        if (attempt < maxRetries) {
          // Aguardar antes da próxima tentativa
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
      }
    }

    throw new Error(lastError?.response?.data?.error || 'Erro ao obter estatísticas após múltiplas tentativas');
  }
}

export const mercadopagoService = new MercadoPagoService();
export default mercadopagoService;

